import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft, Check, FileSpreadsheet, Pencil, Plus, Printer, Trash2, X } from 'lucide-react';
import type { Boq, BoqItem, ContractPeople, Currency, ContractStatement, InvoiceList, InvoiceMatrix, Obligation, ObligationList, ServiceContract, ServiceContractStatus, Variation, VariationList } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, ConfirmDialog, EmptyState, Modal, PageHeader, TableSkeleton, cn } from '../../components/ui';
import { AttachmentsPanel, NotesThread, TeamPicker, VisibilityControl } from '../../components/records';
import { BoqGroupForm, BoqItemForm, ContractForm, InvoiceForm, ObligationForm, ReasonDialog, VariationForm } from './forms';
import { StatementView } from './reports';
import { parseContractSearch, type ContractSearch } from './search';
import { ContractStatusBadge, FiguresPanel, InvoiceStatusBadge, KindBadge, Money, ObligationLine, VARIATION_TONE, clientName, fmtDate, useMoney, useQuantity, usesRetention } from './shared';

const TABS = ['overview', 'boq', 'variations', 'invoices', 'statement', 'followup', 'people', 'documents'] as const;
const NEXT_STATUS: Record<ServiceContractStatus, ServiceContractStatus[]> = { draft: ['active'], active: ['on_hold', 'completed', 'terminated'], on_hold: ['active', 'completed', 'terminated'], completed: [], terminated: [] };

/** One contract: its terms and figures, the bill of quantities, variation orders, invoices, follow-ups and its people. */
export function ContractPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = parseContractSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const tab = search.tab ?? 'overview';
  const setTab = (next: string) => void navigate({ to: '/contracts/$id', params: { id }, search: { ...search, tab: next === 'overview' ? undefined : next } as ContractSearch });
  const contract = useQuery({ queryKey: ['contracts', id], queryFn: () => api.get<ServiceContract>(`/api/contracts/${id}`) });
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [transition, setTransition] = useState<ServiceContractStatus | null>(null);
  const move = useMutation({
    mutationFn: (to: ServiceContractStatus) => api.post<ServiceContract>(`/api/contracts/${id}/transition`, { to }),
    onSuccess: () => { toast.success(t('contracts.saved')); void qc.invalidateQueries({ queryKey: ['contracts'] }); setTransition(null); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/contracts/${id}`),
    onSuccess: () => { toast.success(t('contracts.deleted')); void qc.invalidateQueries({ queryKey: ['contracts'] }); void navigate({ to: '/contracts', search: {} }); },
    onError: (e) => toast.error(describeError(e, t)),
  });

  if (contract.isLoading) return <TableSkeleton />;
  if (!contract.data) return <div className="card p-10 text-center text-sm text-neutral-500">{t('errors.notFound')}</div>;
  const c = contract.data;
  const canManage = c.can_manage;

  return (
    <div>
      <nav className="no-print mb-2 text-xs text-neutral-500"><Link to="/contracts" search={{}} className="hover:underline">{t('contracts.pageTitle')}</Link></nav>
      <PageHeader
        title={pick(i18n.language, c.title_en, c.title_ar)}
        subtitle={`${c.reference} · ${clientName(c, i18n.language)}`}
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => void navigate({ to: '/contracts', search: {} })}><ArrowLeft size={16} className="rtl:rotate-180" /> {t('app.back')}</button>
            {canManage && NEXT_STATUS[c.status].map((to) => (
              <button key={to} type="button" className={cn(to === 'active' ? 'btn-primary' : 'btn-secondary')} onClick={() => setTransition(to)}>{t(`contracts.transitions.${to}`)}</button>
            ))}
            {canManage && c.status === 'draft' && <button type="button" className="btn-danger" onClick={() => setConfirmDelete(true)}><Trash2 size={16} /> {t('app.delete')}</button>}
            {canManage && <button type="button" className="btn-secondary" onClick={() => setEditing(true)}><Pencil size={16} /> {t('app.edit')}</button>}
          </>
        }
      />
      <div className="card mb-3 p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <ContractStatusBadge status={c.status} />
          {c.type && <Badge>{pick(i18n.language, c.type.name_en, c.type.name_ar)}</Badge>}
          <Badge tone="neutral">{c.currency}</Badge>
          <span className="text-xs text-neutral-500" dir="ltr">{fmtDate(c.start_date, i18n.language)} → {fmtDate(c.revised_end_date ?? c.end_date, i18n.language, t('contracts.openEnded'))}</span>
          {c.figures.boq_differs && <Badge tone="warn">{t('contracts.boqDiffers')}</Badge>}
          {c.figures.variations_pending > 0 && <Badge tone="warn">{t('contracts.variationsPending', { count: c.figures.variations_pending })}</Badge>}
          {c.figures.obligations_overdue > 0 && <Badge tone="danger">{t('contracts.followupsOverdue', { count: c.figures.obligations_overdue })}</Badge>}
        </div>
        <FiguresPanel figures={c.figures} currency={c.currency} retention={usesRetention(c)} />
      </div>
      <div className="no-print mb-3 flex flex-wrap gap-1">
        {TABS.map((code) => (
          <button key={code} type="button" className={cn('btn-secondary', tab === code && 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-200')} onClick={() => setTab(code)}>
            {t(`contracts.tabs.${code}`)}
          </button>
        ))}
      </div>
      {tab === 'overview' && <Overview c={c} />}
      {tab === 'boq' && <BoqTab c={c} />}
      {tab === 'variations' && <VariationsTab c={c} />}
      {tab === 'invoices' && <InvoicesTab c={c} />}
      {tab === 'statement' && <StatementTab c={c} />}
      {tab === 'followup' && <FollowupTab c={c} />}
      {tab === 'people' && <PeopleTab c={c} />}
      {tab === 'documents' && <DocumentsTab c={c} />}
      <ContractForm open={editing} onClose={() => setEditing(false)} contract={c} />
      <ConfirmDialog open={confirmDelete} title={t('contracts.deleteContract')} body={t('contracts.deleteContractBody')} danger onCancel={() => setConfirmDelete(false)} onConfirm={() => { setConfirmDelete(false); remove.mutate(); }} />
      <ConfirmDialog open={transition !== null} title={t(`contracts.transitions.${transition ?? 'active'}`)} body={t(`contracts.transitionBody.${transition ?? 'active'}`)} onCancel={() => setTransition(null)} onConfirm={() => transition && move.mutate(transition)} />
    </div>
  );
}

function Overview({ c }: { c: ServiceContract }) {
  const { t, i18n } = useTranslation();
  const fact = (label: string, value: React.ReactNode) => (
    <div><div className="label">{label}</div><div className="text-sm" dir="auto">{value || '—'}</div></div>
  );
  const pct = (v: number) => `${new Intl.NumberFormat(i18n.language).format(v)}%`;
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="grid gap-4">
        <div className="card p-4">
          <h2 className="mb-3 text-sm font-semibold">{t('contracts.terms')}</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {fact(t('contracts.vatPercent'), pct(c.vat_percent))}
            {usesRetention(c) && fact(t('contracts.retentionPercent'), pct(c.retention_percent))}
            {fact(t('contracts.advancePercent'), pct(c.advance_percent))}
            {fact(t('contracts.bondPercent'), pct(c.bond_percent))}
            {fact(t('contracts.paymentDays'), String(c.payment_days))}
            {fact(t('contracts.warrantyMonths'), c.warranty_months === null ? '—' : String(c.warranty_months))}
            {fact(t('contracts.owner'), c.owner ? pick(i18n.language, c.owner.display_name_en, c.owner.display_name_ar) : '—')}
            {fact(t('contracts.agreedValueExVat'), c.figures.agreed_value_minor === null ? '—' : <Money minor={c.figures.agreed_value_minor} currency={c.currency} />)}
            {fact(t('contracts.figures.revisedValue'), <Money minor={c.figures.revised_value_minor} currency={c.currency} />)}
            {fact(t('contracts.figures.revisedWithVat'), <Money minor={c.figures.revised_with_vat_minor} currency={c.currency} />)}
            {fact(t('contracts.people'), String(c.people))}
          </div>
          {c.figures.boq_differs && <p className="mt-3 rounded-md bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">{t('contracts.boqDiffersHint')}</p>}
        </div>
        {(c.payment_terms || c.retention_release_condition || c.penalties || c.notes) && (
          <div className="card grid gap-3 p-4 text-sm">
            {c.payment_terms && <div><div className="label">{t('contracts.paymentTerms')}</div><p className="whitespace-pre-wrap" dir="auto">{c.payment_terms}</p></div>}
            {c.retention_release_condition && <div><div className="label">{t('contracts.retentionRelease')}</div><p className="whitespace-pre-wrap" dir="auto">{c.retention_release_condition}</p></div>}
            {c.penalties && <div><div className="label">{t('contracts.penalties')}</div><p className="whitespace-pre-wrap" dir="auto">{c.penalties}</p></div>}
            {c.notes && <div><div className="label">{t('contracts.notes')}</div><p className="whitespace-pre-wrap" dir="auto">{c.notes}</p></div>}
          </div>
        )}
      </div>
      <div className="grid content-start gap-4">
        <VisibilityControl ownerType="service_contract" ownerId={c.id} />
        <TeamPicker ownerType="service_contract" ownerId={c.id} canEdit={c.can_manage} title={t('contracts.team')} />
      </div>
    </div>
  );
}

function BoqTab({ c }: { c: ServiceContract }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const boq = useQuery({ queryKey: ['contracts', c.id, 'boq'], queryFn: () => api.get<Boq>(`/api/contracts/${c.id}/boq`) });
  const [adding, setAdding] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  const [editing, setEditing] = useState<BoqItem | null>(null);
  const [removing, setRemoving] = useState<BoqItem | null>(null);
  const money = useMoney(c.currency);
  const qty = useQuantity();
  const remove = useMutation({
    mutationFn: (item: BoqItem) => api.delete(`/api/contracts/${c.id}/boq/items/${item.id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contracts', c.id] }); setRemoving(null); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  if (boq.isLoading) return <TableSkeleton />;
  const data = boq.data;
  if (!data) return null;
  const groupName = (id: string | null) => {
    const g = data.groups.find((x) => x.id === id);
    return g ? `${g.code} · ${pick(i18n.language, g.name_en, g.name_ar)}` : t('contracts.ungrouped');
  };
  const byGroup = [...new Set(data.items.map((i) => i.group_id))].map((gid) => ({ gid, items: data.items.filter((i) => i.group_id === gid) }));

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-neutral-500">{data.locked ? t('contracts.boqLocked') : t('contracts.boqDraftHint')}</p>
        {c.can_manage && !data.locked && (
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={() => setAddingGroup(true)}><Plus size={16} /> {t('contracts.newGroup')}</button>
            <button type="button" className="btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> {t('contracts.newItem')}</button>
          </div>
        )}
      </div>
      {!data.items.length ? (
        <EmptyState>{t('contracts.noItems')}</EmptyState>
      ) : (
        byGroup.map(({ gid, items }) => (
          <div key={gid ?? 'none'} className="card overflow-x-auto">
            {data.groups.length > 0 && <div className="border-b border-neutral-200 px-4 py-2 text-sm font-medium dark:border-neutral-800" dir="auto">{groupName(gid)}</div>}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-neutral-500">
                  <th className="p-2 text-start">{t('contracts.itemCode')}</th>
                  <th className="p-2 text-start">{t('contracts.description')}</th>
                  <th className="p-2 text-end">{t('contracts.quantity')}</th>
                  <th className="p-2 text-end">{t('contracts.unitRate')}</th>
                  <th className="p-2 text-end">{t('contracts.amount')}</th>
                  <th className="p-2 text-end">{t('contracts.figures.withVat')}</th>
                  <th className="p-2 text-end">{t('contracts.revisedQuantity')}</th>
                  <th className="p-2 text-end">{t('contracts.certifiedQuantity')}</th>
                  <th className="p-2 text-end">{t('contracts.remaining')}</th>
                  {c.can_manage && !data.locked && <th className="p-2" />}
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className="border-t border-neutral-200 dark:border-neutral-800">
                    <td className="p-2 font-medium" dir="ltr">{i.code}</td>
                    <td className="p-2" dir="auto">
                      {pick(i18n.language, i.description_en, i.description_ar)}
                      {i.added_by_variation && <Badge tone="info">{t('contracts.fromVariation', { number: i.added_by_variation.number ?? '—' })}</Badge>}
                      <span className="block text-xs text-neutral-500">{i.unit}</span>
                    </td>
                    <td className="p-2 text-end tabular-nums" dir="ltr">{qty(i.quantity)}</td>
                    <td className="p-2 text-end tabular-nums" dir="ltr">{money(i.unit_rate_minor, { sign: false })}</td>
                    <td className="p-2 text-end tabular-nums" dir="ltr">{money(i.amount_minor, { sign: false })}</td>
                    <td className="p-2 text-end tabular-nums text-neutral-500" dir="ltr">{money(i.amount_with_vat_minor, { sign: false })}</td>
                    <td className="p-2 text-end tabular-nums" dir="ltr">
                      {qty(i.revised_quantity)}
                      {i.variation_quantity !== 0 && <span className={cn('ms-1 text-xs', i.variation_quantity > 0 ? 'text-emerald-600' : 'text-rose-600')}>{i.variation_quantity > 0 ? '+' : ''}{qty(i.variation_quantity)}</span>}
                    </td>
                    <td className="p-2 text-end tabular-nums" dir="ltr">{qty(i.certified_quantity)}</td>
                    <td className="p-2 text-end tabular-nums" dir="ltr">
                      {qty(i.remaining_quantity)}
                      <Badge tone={i.state === 'done' ? 'brand' : i.state === 'partial' ? 'info' : 'neutral'}>{t(`contracts.itemStates.${i.state}`)}</Badge>
                    </td>
                    {c.can_manage && !data.locked && (
                      <td className="p-2 text-end">
                        <span className="flex justify-end gap-1">
                          <button type="button" className="btn-secondary px-1.5 py-0.5 text-xs" onClick={() => setEditing(i)} aria-label={t('app.edit')}><Pencil size={12} /></button>
                          <button type="button" className="btn-secondary px-1.5 py-0.5 text-xs" onClick={() => setRemoving(i)} aria-label={t('app.delete')}><Trash2 size={12} /></button>
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
      <BoqTotals totals={data.totals} currency={c.currency} />
      <BoqItemForm open={adding} onClose={() => setAdding(false)} contractId={c.id} groups={data.groups} currency={c.currency} />
      <BoqItemForm open={editing !== null} onClose={() => setEditing(null)} contractId={c.id} groups={data.groups} item={editing ?? undefined} currency={c.currency} />
      <BoqGroupForm open={addingGroup} onClose={() => setAddingGroup(false)} contractId={c.id} groups={data.groups} />
      <ConfirmDialog open={removing !== null} title={t('contracts.deleteItem')} danger onCancel={() => setRemoving(null)} onConfirm={() => removing && remove.mutate(removing)} />
    </div>
  );
}

/** The bill's total without VAT, the VAT on it at the contract's rate, and the total with VAT (owner rule: both amounts close the contract). */
function BoqTotals({ totals, currency }: { totals: Boq['totals']; currency: Currency }) {
  const { t, i18n } = useTranslation();
  const changed = totals.revised_amount_minor !== totals.amount_minor;
  const row = (label: string, minor: number, strong?: boolean) => (
    <div className="flex items-center justify-between gap-4"><span className={cn(strong && 'font-semibold')}>{label}</span><Money minor={minor} currency={currency} className={cn('tabular-nums', strong && 'font-semibold')} /></div>
  );
  return (
    <div className="card grid gap-1 p-4 text-sm sm:min-w-[24rem] sm:justify-self-end">
      {row(`${changed ? t('contracts.figures.revisedValue') : t('contracts.total')} (${t('contracts.figures.withoutVat')})`, totals.revised_amount_minor)}
      {changed && <p className="text-xs text-neutral-500">{t('contracts.figures.originalValue')}: <Money minor={totals.amount_minor} currency={currency} /> · {t('contracts.figures.variations')}: <Money minor={totals.revised_amount_minor - totals.amount_minor} currency={currency} /></p>}
      {row(`${t('contracts.figures.vat')} (${new Intl.NumberFormat(i18n.language).format(totals.vat_percent)}%)`, totals.revised_vat_minor)}
      {row(`${t('contracts.total')} (${t('contracts.figures.withVat')})`, totals.revised_amount_with_vat_minor, true)}
      <p className="mt-1 text-xs text-neutral-500">{t('contracts.figures.certified')}: <Money minor={totals.certified_amount_minor} currency={currency} /></p>
    </div>
  );
}

function VariationsTab({ c }: { c: ServiceContract }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['contracts', c.id, 'variations'], queryFn: () => api.get<VariationList>(`/api/contracts/${c.id}/variations`) });
  const boq = useQuery({ queryKey: ['contracts', c.id, 'boq'], queryFn: () => api.get<Boq>(`/api/contracts/${c.id}/boq`) });
  const [adding, setAdding] = useState(false);
  const [editingLines, setEditingLines] = useState<Variation | null>(null);
  const [addingItem, setAddingItem] = useState<Variation | null>(null);
  const [deciding, setDeciding] = useState<{ v: Variation; approve: boolean } | null>(null);
  const [removing, setRemoving] = useState<Variation | null>(null);
  const qty = useQuantity();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['contracts', c.id] });
  const submit = useMutation({ mutationFn: (v: Variation) => api.post(`/api/contracts/${c.id}/variations/${v.id}/submit`), onSuccess: invalidate, onError: (e) => toast.error(describeError(e, t)) });
  const decide = useMutation({
    mutationFn: ({ v, approve, decided_on, note }: { v: Variation; approve: boolean; decided_on: string; note: string }) => api.post(`/api/contracts/${c.id}/variations/${v.id}/decision`, { approve, decided_on, note: note || null }),
    onSuccess: () => { invalidate(); setDeciding(null); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const remove = useMutation({ mutationFn: (v: Variation) => api.delete(`/api/contracts/${c.id}/variations/${v.id}`), onSuccess: () => { invalidate(); setRemoving(null); }, onError: (e) => toast.error(describeError(e, t)) });
  if (list.isLoading) return <TableSkeleton />;
  const items = list.data?.items ?? [];
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-neutral-500">{t('contracts.variationsHint')}</p>
        {c.can_manage && c.status !== 'draft' && <button type="button" className="btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> {t('contracts.newVariation')}</button>}
      </div>
      {!items.length ? (
        <EmptyState>{c.status === 'draft' ? t('contracts.noVariationsDraft') : t('contracts.noVariations')}</EmptyState>
      ) : (
        items.map((v) => (
          <div key={v.id} className="card p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium" dir="auto">{v.number !== null ? `#${v.number} · ` : ''}{pick(i18n.language, v.title_en, v.title_ar)}</span>
                  <Badge tone={VARIATION_TONE[v.status]}>{t(`contracts.variationStatuses.${v.status}`)}</Badge>
                  <Money minor={v.value_minor} currency={c.currency} className="text-sm font-semibold" />
                </div>
                {v.reason && <p className="mt-1 text-xs text-neutral-500" dir="auto">{v.reason}</p>}
                {v.decided_on && <p className="mt-1 text-xs text-neutral-500">{t('contracts.decidedOn')}: {fmtDate(v.decided_on, i18n.language)}{v.decision_note ? ` — ${v.decision_note}` : ''}</p>}
              </div>
              {c.can_manage && (
                <div className="flex flex-wrap gap-1">
                  {v.status === 'draft' && <button type="button" className="btn-secondary px-2 py-0.5 text-xs" onClick={() => setEditingLines(v)}>{t('contracts.editQuantities')}</button>}
                  {v.status === 'draft' && <button type="button" className="btn-secondary px-2 py-0.5 text-xs" onClick={() => setAddingItem(v)}><Plus size={12} /> {t('contracts.newItem')}</button>}
                  {v.status === 'draft' && v.lines.length > 0 && <button type="button" className="btn-primary px-2 py-0.5 text-xs" onClick={() => submit.mutate(v)}>{t('contracts.submitVariation')}</button>}
                  {v.status === 'draft' && <button type="button" className="btn-secondary px-1.5 py-0.5 text-xs" onClick={() => setRemoving(v)} aria-label={t('app.delete')}><Trash2 size={12} /></button>}
                  {v.status === 'submitted' && <button type="button" className="btn-primary px-2 py-0.5 text-xs" onClick={() => setDeciding({ v, approve: true })}><Check size={12} /> {t('contracts.approve')}</button>}
                  {v.status === 'submitted' && <button type="button" className="btn-secondary px-2 py-0.5 text-xs" onClick={() => setDeciding({ v, approve: false })}><X size={12} /> {t('contracts.reject')}</button>}
                </div>
              )}
            </div>
            {v.lines.length > 0 && (
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-neutral-500">
                    <th className="p-1 text-start">{t('contracts.itemCode')}</th>
                    <th className="p-1 text-start">{t('contracts.description')}</th>
                    <th className="p-1 text-end">{t('contracts.quantityChange')}</th>
                    <th className="p-1 text-end">{t('contracts.unitRate')}</th>
                    <th className="p-1 text-end">{t('contracts.value')}</th>
                  </tr>
                </thead>
                <tbody>
                  {v.lines.map((l) => (
                    <tr key={l.boq_item_id} className="border-t border-neutral-200 dark:border-neutral-800">
                      <td className="p-1" dir="ltr">{l.code}{l.new_item && <Badge tone="info">{t('contracts.newItemBadge')}</Badge>}</td>
                      <td className="p-1" dir="auto">{pick(i18n.language, l.description_en, l.description_ar)}</td>
                      <td className={cn('p-1 text-end tabular-nums', l.quantity_change < 0 && 'text-rose-600')} dir="ltr">{l.quantity_change > 0 ? '+' : ''}{qty(l.quantity_change)} {l.unit}</td>
                      <td className="p-1 text-end tabular-nums" dir="ltr"><Money minor={l.unit_rate_minor} currency={c.currency} sign={false} /></td>
                      <td className="p-1 text-end tabular-nums" dir="ltr"><Money minor={l.value_minor} currency={c.currency} sign={false} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))
      )}
      <VariationForm open={adding} onClose={() => setAdding(false)} contractId={c.id} />
      {editingLines && boq.data && <VariationLinesDialog contract={c} variation={editingLines} items={boq.data.items} onClose={() => setEditingLines(null)} />}
      {addingItem && boq.data && <BoqItemForm open onClose={() => setAddingItem(null)} contractId={c.id} groups={boq.data.groups} variationId={addingItem.id} currency={c.currency} />}
      <ReasonDialog
        open={deciding !== null}
        onClose={() => setDeciding(null)}
        title={deciding?.approve ? t('contracts.approveVariation') : t('contracts.rejectVariation')}
        label={t('contracts.decisionNote')}
        dateLabel={t('contracts.decidedOn')}
        pending={decide.isPending}
        onSubmit={({ text, date }) => deciding && decide.mutate({ v: deciding.v, approve: deciding.approve, decided_on: date, note: text })}
      />
      <ConfirmDialog open={removing !== null} title={t('contracts.deleteVariation')} danger onCancel={() => setRemoving(null)} onConfirm={() => removing && remove.mutate(removing)} />
    </div>
  );
}

/** The quantity changes of a draft variation order: one row per item, positive adds and negative removes. */
function VariationLinesDialog({ contract, variation, items, onClose }: { contract: ServiceContract; variation: Variation; items: BoqItem[]; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(items.map((i) => [i.id, String(variation.lines.find((l) => l.boq_item_id === i.id)?.quantity_change ?? '')])));
  const save = useMutation({
    mutationFn: () => api.put(`/api/contracts/${contract.id}/variations/${variation.id}/lines`, { lines: items.filter((i) => Number(values[i.id] || 0) !== 0).map((i) => ({ boq_item_id: i.id, quantity_change: Number(values[i.id]) })) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contracts', contract.id] }); onClose(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  return (
    <Modal open onClose={onClose} wide title={t('contracts.editQuantities')} footer={<><button type="button" className="btn-secondary" onClick={onClose}>{t('app.cancel')}</button><button type="button" className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>{t('app.save')}</button></>}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase text-neutral-500">
            <th className="p-1 text-start">{t('contracts.itemCode')}</th>
            <th className="p-1 text-start">{t('contracts.description')}</th>
            <th className="p-1 text-end">{t('contracts.quantity')}</th>
            <th className="p-1 text-end">{t('contracts.quantityChange')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className="border-t border-neutral-200 dark:border-neutral-800">
              <td className="p-1" dir="ltr">{i.code}</td>
              <td className="p-1" dir="auto">{pick(i18n.language, i.description_en, i.description_ar)}</td>
              <td className="p-1 text-end tabular-nums" dir="ltr">{i.revised_quantity} {i.unit}</td>
              <td className="p-1 text-end"><input className="input w-28 text-end" type="number" step="0.0001" dir="ltr" value={values[i.id] ?? ''} onChange={(e) => setValues({ ...values, [i.id]: e.target.value })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}

function InvoicesTab({ c }: { c: ServiceContract }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const list = useQuery({ queryKey: ['contracts', c.id, 'invoices'], queryFn: () => api.get<InvoiceList>(`/api/contracts/${c.id}/invoices`) });
  const money = useMoney(c.currency);
  const retention = usesRetention(c);
  if (list.isLoading) return <TableSkeleton />;
  const items = list.data?.items ?? [];
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-neutral-500">{t('contracts.invoicesHint')}</p>
        {c.can_manage && c.status !== 'draft' && <button type="button" className="btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> {t('contracts.newInvoice')}</button>}
      </div>
      {!items.length ? (
        <EmptyState>{t('contracts.noInvoices')}</EmptyState>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-neutral-500">
                <th className="p-2 text-start">{t('contracts.invoice')}</th>
                <th className="p-2 text-start">{t('contracts.invoiceDate')}</th>
                <th className="p-2 text-end">{t('contracts.figures.previousCumulative')}</th>
                <th className="p-2 text-end">{t('contracts.thisInvoice')}</th>
                {retention && <th className="p-2 text-end">{t('contracts.figures.retention')}</th>}
                <th className="p-2 text-end">{t('contracts.figures.vat')}</th>
                <th className="p-2 text-end">{t('contracts.figures.netDue')}</th>
                <th className="p-2 text-end">{t('contracts.figures.outstanding')}</th>
                <th className="p-2 text-start">{t('contracts.status')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="cursor-pointer border-t border-neutral-200 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-800/40" onClick={() => void navigate({ to: '/contracts/$id/invoices/$invoiceId', params: { id: c.id, invoiceId: i.id } })}>
                  <td className="p-2">
                    <span className="font-medium tabular-nums" dir="ltr">{i.number === null ? t('contracts.draftInvoice') : `#${i.number}`}</span>
                    <span className="ms-2"><KindBadge kind={i.kind} /></span>
                  </td>
                  <td className="p-2 text-xs" dir="ltr">
                    {fmtDate(i.invoice_date, i18n.language)}
                    {i.due_date && <span className="block text-neutral-500">{t('contracts.dueShort')} {fmtDate(i.due_date, i18n.language)}</span>}
                  </td>
                  <td className="p-2 text-end tabular-nums" dir="ltr">{money(i.previous_gross_minor, { sign: false })}</td>
                  <td className="p-2 text-end tabular-nums" dir="ltr">{money(i.kind === 'progress' ? i.gross_minor : (i.amount_minor ?? i.gross_minor), { sign: false })}</td>
                  {retention && <td className="p-2 text-end tabular-nums" dir="ltr">{money(i.retention_minor, { sign: false })}</td>}
                  <td className="p-2 text-end tabular-nums" dir="ltr">{money(i.vat_minor, { sign: false })}</td>
                  <td className="p-2 text-end font-medium tabular-nums" dir="ltr">{money(i.net_due_minor, { sign: false })}</td>
                  <td className={cn('p-2 text-end tabular-nums', i.aging && i.aging !== 'current' && 'text-rose-600 dark:text-rose-400')} dir="ltr">{money(i.outstanding_minor, { sign: false })}</td>
                  <td className="p-2"><InvoiceStatusBadge status={i.status} />{i.problems.length > 0 && <Badge tone="warn">{t('contracts.needsAttention')}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <InvoiceForm open={adding} onClose={() => setAdding(false)} contractId={c.id} currency={c.currency} onCreated={(id) => void navigate({ to: '/contracts/$id/invoices/$invoiceId', params: { id: c.id, invoiceId: id } })} />
    </div>
  );
}

/** The statement of account and MRLCO's invoice matrix: one page for screen, print and Excel. */
function StatementTab({ c }: { c: ServiceContract }) {
  const { t, i18n } = useTranslation();
  const [showMatrix, setShowMatrix] = useState(false);
  const statement = useQuery({ queryKey: ['contracts', c.id, 'statement'], queryFn: () => api.get<ContractStatement>(`/api/contracts/${c.id}/statement`) });
  const matrix = useQuery({ queryKey: ['contracts', c.id, 'matrix'], queryFn: () => api.get<InvoiceMatrix>(`/api/contracts/${c.id}/matrix`), enabled: showMatrix });
  const locale = i18n.language.startsWith('ar') ? 'ar' : 'en';
  if (statement.isLoading) return <TableSkeleton />;
  if (!statement.data) return null;
  return (
    <div className="grid gap-3">
      <div className="no-print flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          <button type="button" className={cn('btn-secondary', !showMatrix && 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-200')} onClick={() => setShowMatrix(false)}>{t('contracts.statement')}</button>
          <button type="button" className={cn('btn-secondary', showMatrix && 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-200')} onClick={() => setShowMatrix(true)}>{t('contracts.matrix')}</button>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={() => window.print()}><Printer size={16} /> {t('contracts.print')}</button>
          <button type="button" className="btn-secondary" onClick={() => void api.download(`/api/contracts/${c.id}/${showMatrix ? 'matrix' : 'statement'}/export${qs({ locale })}`)}><FileSpreadsheet size={16} /> {t('contracts.exportExcel')}</button>
        </div>
      </div>
      {showMatrix ? <MatrixView data={matrix.data} /> : <StatementView statement={statement.data} />}
    </div>
  );
}

function MatrixView({ data }: { data: InvoiceMatrix | undefined }) {
  const { t, i18n } = useTranslation();
  const money = useMoney(data?.contract.currency);
  const qty = useQuantity();
  if (!data) return <TableSkeleton />;
  if (!data.invoices.length) return <EmptyState>{t('contracts.noIssuedInvoices')}</EmptyState>;
  return (
    <div className="card overflow-x-auto p-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="uppercase text-neutral-500">
            <th className="p-1 text-start">{t('contracts.itemCode')}</th>
            <th className="p-1 text-start">{t('contracts.description')}</th>
            <th className="p-1 text-end">{t('contracts.revisedQuantity')}</th>
            {data.invoices.map((i) => <th key={i.id} className="p-1 text-end" dir="ltr">#{i.number}<span className="block font-normal">{fmtDate(i.invoice_date, i18n.language)}</span></th>)}
            <th className="p-1 text-end">{t('contracts.figures.certified')}</th>
            <th className="p-1 text-end">{t('contracts.remaining')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.item.id} className="border-t border-neutral-200 dark:border-neutral-800">
              <td className="p-1" dir="ltr">{r.item.code}</td>
              <td className="p-1" dir="auto">{pick(i18n.language, r.item.description_en, r.item.description_ar)}</td>
              <td className="p-1 text-end tabular-nums" dir="ltr">{qty(r.item.revised_quantity)} {r.item.unit}</td>
              {r.cells.map((cell, n) => (
                <td key={data.invoices[n]?.id ?? n} className="p-1 text-end tabular-nums" dir="ltr">
                  {cell.quantity ? <>{qty(cell.quantity)}<span className="block text-neutral-500">{money(cell.amount_minor, { sign: false })}</span></> : <span className="text-neutral-300">—</span>}
                </td>
              ))}
              <td className="p-1 text-end tabular-nums" dir="ltr">{qty(r.quantity)}<span className="block text-neutral-500">{money(r.amount_minor, { sign: false })}</span></td>
              <td className="p-1 text-end tabular-nums" dir="ltr">{qty(r.remaining_quantity)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-neutral-300 font-semibold dark:border-neutral-700">
            <td className="p-1" colSpan={3}>{t('contracts.total')}</td>
            {data.totals.cells.map((amount, n) => <td key={data.invoices[n]?.id ?? n} className="p-1 text-end tabular-nums" dir="ltr">{money(amount, { sign: false })}</td>)}
            <td className="p-1 text-end tabular-nums" dir="ltr">{money(data.totals.amount_minor, { sign: false })}</td>
            <td className="p-1" />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function FollowupTab({ c }: { c: ServiceContract }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['contracts', c.id, 'obligations'], queryFn: () => api.get<ObligationList>(`/api/contracts/obligations?contract_id=${c.id}`) });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Obligation | null>(null);
  const [waiving, setWaiving] = useState<Obligation | null>(null);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['contracts'] });
  const close = useMutation({
    mutationFn: ({ o, status, note }: { o: Obligation; status: 'done' | 'waived'; note?: string }) => api.post(`/api/contracts/${c.id}/obligations/${o.id}/close`, { status, note: note || null }),
    onSuccess: () => { invalidate(); setWaiving(null); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const remove = useMutation({ mutationFn: (o: Obligation) => api.delete(`/api/contracts/${c.id}/obligations/${o.id}`), onSuccess: invalidate, onError: (e) => toast.error(describeError(e, t)) });
  if (list.isLoading) return <TableSkeleton />;
  const items = list.data?.items ?? [];
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-neutral-500">{t('contracts.followupHint')}</p>
        {c.can_manage && <button type="button" className="btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> {t('contracts.newObligation')}</button>}
      </div>
      {!items.length ? (
        <EmptyState>{t('contracts.noObligations')}</EmptyState>
      ) : (
        <ul className="card divide-y divide-neutral-200 dark:divide-neutral-800">
          {items.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <Badge tone={o.status === 'open' ? ((o.days_to_due ?? 0) < 0 ? 'danger' : 'info') : 'neutral'}>{t(`contracts.obligationStatuses.${o.status}`)}</Badge>
                <ObligationLine o={o} />
              </div>
              <div className="flex flex-wrap gap-1">
                {o.status === 'open' && o.can_edit && <button type="button" className="btn-secondary px-2 py-0.5 text-xs" onClick={() => close.mutate({ o, status: 'done' })}><Check size={12} /> {t('contracts.markDone')}</button>}
                {o.status === 'open' && c.can_manage && <button type="button" className="btn-secondary px-2 py-0.5 text-xs" onClick={() => setWaiving(o)}>{t('contracts.waive')}</button>}
                {o.status === 'open' && c.can_manage && <button type="button" className="btn-secondary px-1.5 py-0.5 text-xs" onClick={() => setEditing(o)} aria-label={t('app.edit')}><Pencil size={12} /></button>}
                {c.can_manage && <button type="button" className="btn-secondary px-1.5 py-0.5 text-xs" onClick={() => remove.mutate(o)} aria-label={t('app.delete')}><Trash2 size={12} /></button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      <ObligationForm open={adding} onClose={() => setAdding(false)} contractId={c.id} />
      <ObligationForm open={editing !== null} onClose={() => setEditing(null)} contractId={c.id} obligation={editing ?? undefined} />
      <ReasonDialog open={waiving !== null} onClose={() => setWaiving(null)} title={t('contracts.waive')} label={t('contracts.note')} pending={close.isPending} onSubmit={({ text }) => waiving && close.mutate({ o: waiving, status: 'waived', note: text })} />
    </div>
  );
}

function PeopleTab({ c }: { c: ServiceContract }) {
  const { t, i18n } = useTranslation();
  const people = useQuery({ queryKey: ['contracts', c.id, 'people'], queryFn: () => api.get<ContractPeople>(`/api/contracts/${c.id}/people`) });
  const { can } = useAuth();
  if (people.isLoading) return <TableSkeleton />;
  const items = people.data?.items ?? [];
  if (!items.length) return <EmptyState>{t('contracts.noPeople')}</EmptyState>;
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase text-neutral-500">
            <th className="p-2 text-start">{t('contracts.person')}</th>
            <th className="p-2 text-start">{t('contracts.entity')}</th>
            <th className="p-2 text-start">{t('contracts.role')}</th>
            <th className="p-2 text-end">{t('contracts.percentage')}</th>
            <th className="p-2 text-start">{t('contracts.period')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.allocation_id} className="border-t border-neutral-200 dark:border-neutral-800">
              <td className="p-2" dir="auto">
                {can('people') ? <Link to="/people/$id" params={{ id: p.employee.id }} className="hover:underline">{pick(i18n.language, p.employee.display_name_en, p.employee.display_name_ar)}</Link> : pick(i18n.language, p.employee.display_name_en, p.employee.display_name_ar)}
                {p.employee.employee_no && <span className="block text-xs text-neutral-500" dir="ltr">{p.employee.employee_no}</span>}
              </td>
              <td className="p-2" dir="auto">{pick(i18n.language, p.entity.name_en, p.entity.name_ar)}</td>
              <td className="p-2">{t(`people.roles.${p.role}`, { defaultValue: p.role })}</td>
              <td className="p-2 text-end tabular-nums" dir="ltr">{p.percentage}%</td>
              <td className="p-2 text-xs" dir="ltr">
                {fmtDate(p.start_date, i18n.language)} → {fmtDate(p.end_date, i18n.language, t('contracts.openEnded'))}
                <Badge tone={p.state === 'active' ? 'brand' : p.state === 'planned' ? 'info' : 'neutral'}>{t(`people.states.${p.state}`)}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentsTab({ c }: { c: ServiceContract }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <AttachmentsPanel ownerType="service_contract" ownerId={c.id} canEdit={c.can_manage} />
      <NotesThread ownerType="service_contract" ownerId={c.id} canModerate={c.can_manage} />
    </div>
  );
}
